import React, { useContext, useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';

import {
  SalesforceContext,
  Steps,
  AcquiringChannelFlags,
  CustomerTypes,
  ecommerceProductCategoryName,
} from 'src/context/salesforce-context';
import { OnboardingSummaryContext } from 'src/context/onboarding-summary-context';
import DeliveryAddressModal from './delivery-address-modal/delivery-address-modal';
import SolutionSummary from './solution-summary/solution-summary';
import DeliveryAddressesSummary from './delivery-addresses-summary/delivery-addresses-summary';
import BusinessInformationSummary from './business-information-summary/business-information-summary';
import PersonalInformationSummary from './personal-information-summary/personal-information-summary';
import AgentInformation from './agent-information/agent-information';
import ApplicationSummary from 'src/containers/onboarding/create-account/confirm/application-summary/application-summary';
import DeviceQuantityChangeConflictResolver from 'src/containers/onboarding/create-account/device-quantity-reduce-confirm-modal/device-quantity-change-conflict-resolver';
import Accordion from 'src/components/accordion/accordion';
import DocumentViewDownloadButtons, {
  SaveDocument,
} from './document-download-buttons/document-download-buttons';
import TealiumEvents from 'src/helpers/tealium-events';
import { onboardingFailureRoute } from 'src/routing/routes';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import { getValue, urlQueryParams } from 'src/helpers/custom-url-search-param';
import { PendingAppComplete } from '../pending-app-complete/pending-app-complete';
import './confirm.scss';

export default function ConfirmApplication({
  quickNavigate,
  toggleNext,
  triggerNext,
  showPageLoader,
}) {
  const {
    deviceIdToUpdate,
    deviceQuantityToUpdate,
    devicePriceToUpdate,
    setDeviceIdToUpdate,
    setDevicePriceToUpdate,
    setDeviceQuantityToUpdate,
    deviceQuantitiesToDisplay,
    deviceQuantityIsManualUpdate,
    setDeviceQuantityIsManualUpdate,
    totalPrice,
    getDeliveriesToDisplay,
    onceOffCost,
    totalNumberOfDevices,
  } = useContext(OnboardingSummaryContext);
  const {
    merchant,
    setDeviceOrderQuantity,
    setMerchant,
    solutionType,
    isLoadingFromSessionStorage,
    getDeviceOrderQuantity,
    uniqueTerminals,
    customerType,
    lead,
    applicationComplete,
    isVodapayChannel,
    isVodacomBusinessChannel,
  } = useContext(SalesforceContext);
  const [modalInitialDeviceQuantity, setModalInitialDeviceQuantity] = useState(null);
  const [deliveryAddressIsNonDefault, setDeliveryAddressIsNonDefault] = useState(false);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isPostPayment, setIsPostPayment] = useState(false);
  const [manualNextTriggered, setManualNextTriggered] = useState(false);
  const [redirectToRoute, setRedirectToRoute] = useState('');

  useEffect(() => {
    const qp = urlQueryParams(window.location.search);
    if (getValue(qp, 'origin') === 'VPG') {
      setIsPostPayment(true);
    }
  }, []);

  useEffect(() => {
    if (
      isPostPayment &&
      !manualNextTriggered &&
      !isLoadingFromSessionStorage &&
      !!merchant?.opportunity
    ) {
      toggleNext(false);
      showPageLoader(true);
      const qp = urlQueryParams(window.location.search);
      if (getValue(qp, 'status') === 'Success') {
        setMerchant({
          ...merchant,
          opportunity: {
            ...merchant.opportunity,
            online_payment_order_no: getValue(qp, 'orderNo'),
          },
        });
      } else if (getValue(qp, 'status') === 'TRANSACTION_CANCELLED') {
        toggleNext(true);
        showPageLoader(false);
      } else {
        setRedirectToRoute(onboardingFailureRoute);
        showPageLoader(false);
      }
    }
  }, [merchant?.opportunity, isPostPayment, manualNextTriggered, isLoadingFromSessionStorage]);

  useEffect(() => {
    if (merchant?.opportunity?.online_payment_order_no && !isLoadingFromSessionStorage) {
      setManualNextTriggered(true);
      toggleNext(false);
      showPageLoader(true);
      SaveDocument(
        merchant,
        lead,
        getDeliveriesToDisplay,
        getDeviceOrderQuantity,
        uniqueTerminals,
        deviceQuantitiesToDisplay,
        totalPrice,
        onceOffCost,
        customerType,
      );
      triggerNext();
    }
  }, [merchant?.opportunity?.online_payment_order_no, isLoadingFromSessionStorage]);

  useEffect(() => {
    if (merchant?.account?.business_type && !isLoadingFromSessionStorage) {
      const deviceNameList = [];
      const quantityList = [];
      const priceList = [];
      const tenderList = [];

      merchant.opportunity &&
        Object.values(merchant.opportunity.deliveries)
          .filter(
            (delivery) =>
              delivery.number_of_devices > 0 &&
              (!delivery.product_category || delivery.is_product_category),
          )
          .forEach((delivery) => {
            deviceNameList.push(delivery.device_name);
            quantityList.push(delivery.number_of_devices);
            priceList.push(delivery.price);
          });

      merchant.opportunity &&
        Object.values(merchant.opportunity.deliveries)
          .filter(
            (product) =>
              product.number_of_devices > 0 &&
              product.product_category === ecommerceProductCategoryName &&
              !product.is_product_category,
          )
          .forEach((delivery) => {
            tenderList.push(delivery.device_name);
          });

      const assistantId = lead.dealerId || lead.agentId || '';

      TealiumEvents.confirmPageLoadCheckoutStartCombined(
        merchant.account.business_type,
        deviceNameList,
        quantityList,
        priceList,
        customerType,
        assistantId,
        tenderList,
      );
    }
  }, [merchant?.account?.business_type]);

  useEffect(() => {
    if (!isLoadingFromSessionStorage && applicationComplete) {
      SaveDocument(
        merchant,
        lead,
        getDeliveriesToDisplay,
        getDeviceOrderQuantity,
        uniqueTerminals,
        deviceQuantitiesToDisplay,
        totalPrice,
        onceOffCost,
        customerType,
      );
    }
  }, [applicationComplete]);
  const buyInStore =
    solutionType.includes(AcquiringChannelFlags.buyInstoreFicaOnline) &&
    customerType !== CustomerTypes.agent;

  const DesktopHeader = () => (
    <>
      <div className="main-heading">
        <p>
          {(customerType === CustomerTypes.normal && lead.consumerType === CONSUMER_TYPE.owner) ||
          customerType === CustomerTypes.pos
            ? 'Confirm your application'
            : 'Confirm the application'}
        </p>
      </div>
    </>
  );

  const clearDeliveryModal = () => {
    setModalInitialDeviceQuantity(null);
    setDeliveryAddressIsNonDefault(false);
    setShowAddressModal(false);
  };

  const mobileElements = () => {
    const alwaysShowElements =
      customerType !== CustomerTypes.normal && customerType !== CustomerTypes.pos
        ? [
            {
              title: 'Business information',

              componentContainerClass: 'has-padding-left-4',
              component: (
                <BusinessInformationSummary quickNavigate={quickNavigate} isMobile={true} />
              ),
              automationIdDescription: 'Your_Business_Information',
            },
            {
              title: 'Personal information',

              componentContainerClass: 'has-padding-left-7',
              component: (
                <PersonalInformationSummary quickNavigate={quickNavigate} isMobile={true} />
              ),
              automationIdDescription: 'Your_Personal_Information',
            },
            {
              title: 'Agent information',

              componentContainerClass: 'has-padding-left-7',
              component: <AgentInformation isMobile={true} />,
              automationIdDescription: 'Your_Personal_Information',
            },
          ]
        : [
            {
              title:
                lead.consumerType === CONSUMER_TYPE.owner
                  ? 'Your business information'
                  : 'Business information',

              componentContainerClass: 'has-padding-left-4',
              component: (
                <BusinessInformationSummary quickNavigate={quickNavigate} isMobile={true} />
              ),
              automationIdDescription: 'Your_Business_Information',
            },
            {
              title:
                lead.consumerType === CONSUMER_TYPE.owner
                  ? 'Your personal information'
                  : 'Personal information',

              componentContainerClass: 'has-padding-left-7',
              component: (
                <PersonalInformationSummary quickNavigate={quickNavigate} isMobile={true} />
              ),
              automationIdDescription: 'Your_Personal_Information',
            },
          ];
    const onlineOnlyElements = [
      {
        title:
          (customerType === CustomerTypes.normal && lead.consumerType === CONSUMER_TYPE.owner) ||
          customerType === CustomerTypes.pos
            ? 'Your solution'
            : 'Customer solutions',
        component: <SolutionSummary quickNavigate={quickNavigate} isMobile={true} />,
        automationIdDescription: 'Your_Solution',
        onEdit: () => {
          quickNavigate(Steps.CHOOSE_A_SOLUTION);
        },
        hideEdit: isVodacomBusinessChannel,
      },
      {
        title: 'Delivery address',

        component: (
          <DeliveryAddressesSummary
            quickNavigate={quickNavigate}
            setModalInitialDeviceQuantity={setModalInitialDeviceQuantity}
            setDeliveryAddressIsNonDefault={setDeliveryAddressIsNonDefault}
            setShowAddressModal={setShowAddressModal}
            isMobile={true}
          />
        ),
        automationIdDescription: 'Delivery_Address',
        hide: () => isVodapayChannel,
      },
    ];

    const sections = !buyInStore
      ? [...onlineOnlyElements, ...alwaysShowElements]
      : alwaysShowElements;
    return sections.filter((section) => !section.hide || !section.hide());
  };

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  const showDownloadButton = () =>
    !solutionType.includes(AcquiringChannelFlags.buyInstoreFicaOnline) ||
    customerType === CustomerTypes.agent;

  const sovOnly = () => {
    return merchant?.account?.sov_requested && totalNumberOfDevices === 0;
  };

  const isOnlyProductEcommerceCategory = () =>
    Object.values(merchant?.opportunity?.deliveries)
      .filter((product) => product.number_of_devices > 0)
      .every((product) => product.product_category === ecommerceProductCategoryName);

  return merchant.isPendingApp ? (
    <PendingAppComplete />
  ) : (
    <>
      <div className="ConfirmApplication container">
        <DeviceQuantityChangeConflictResolver
          deviceId={deviceIdToUpdate}
          deviceQuantity={deviceQuantityToUpdate}
          deviceQuantityChangeAcceptCallback={(deviceIdToUpdate, deviceQuantity) => {
            const deviceExistingQuantity = getDeviceOrderQuantity(deviceIdToUpdate);
            const deviceName = uniqueTerminals[deviceIdToUpdate].name;
            setDeviceOrderQuantity(deviceIdToUpdate, deviceQuantity, devicePriceToUpdate);
            TealiumEvents.confirmTerminalQuantityChange(
              deviceName,
              devicePriceToUpdate,
              deviceExistingQuantity,
              deviceQuantity,
              deviceQuantityIsManualUpdate,
              customerType,
            );
            setDeviceQuantityToUpdate(null);
            setDeviceIdToUpdate(null);
            setDevicePriceToUpdate(null);
            setDeviceQuantityIsManualUpdate(null);
          }}
          deviceQuantityChangeRejectCallback={() => {
            setDeviceQuantityToUpdate(null);
            setDeviceIdToUpdate(null);
            setDevicePriceToUpdate(null);
            setDeviceQuantityIsManualUpdate(null);
          }}
        />
        <span className="is-hidden-tablet-only is-hidden-desktop-only is-hidden-widescreen-only is-hidden-fullhd">
          <br />
          <Accordion
            showInitial={true}
            elements={mobileElements()}
            automationIdPrefix="Onboarding_Confirm"
          />
          <div className="spacer" />
        </span>

        <span className="is-hidden-mobile">
          <DesktopHeader />
          <div className="columns">
            <div className="column">
              {!buyInStore && (
                <>
                  <SolutionSummary quickNavigate={quickNavigate} />
                  {!isVodapayChannel && !sovOnly() && !isOnlyProductEcommerceCategory() && (
                    <DeliveryAddressesSummary
                      quickNavigate={quickNavigate}
                      setModalInitialDeviceQuantity={setModalInitialDeviceQuantity}
                      setDeliveryAddressIsNonDefault={setDeliveryAddressIsNonDefault}
                      setShowAddressModal={setShowAddressModal}
                    />
                  )}
                </>
              )}
              <BusinessInformationSummary quickNavigate={quickNavigate} />
              <PersonalInformationSummary quickNavigate={quickNavigate} />
              {customerType !== CustomerTypes.normal && customerType !== CustomerTypes.pos && (
                <AgentInformation />
              )}
            </div>
            <div className="column has-padding-left-0 is-4 is-hidden-mobile-only is-hidden-tablet-only">
              <ApplicationSummary
                tablet={false}
                showPrice={!buyInStore && !sovOnly() && !isVodacomBusinessChannel}
              />
              {showDownloadButton() && <DocumentViewDownloadButtons />}
            </div>
          </div>
        </span>

        <span className="is-hidden-not-ipad">
          {showDownloadButton() && <DocumentViewDownloadButtons />}
          <div className="tablet-summary">
            <ApplicationSummary
              tablet={true}
              showPrice={!buyInStore && !isVodacomBusinessChannel}
            />
          </div>
        </span>
      </div>

      <div className="is-hidden-mobile" style={{ height: '103px' }} />

      <DeliveryAddressModal
        show={showAddressModal}
        onCancel={() => clearDeliveryModal()}
        modalInitialDeviceQuantity={modalInitialDeviceQuantity}
        onSubmit={() => clearDeliveryModal()}
        deliveryAddressToEdit={modalInitialDeviceQuantity}
        deliveryAddressIsNonDefault={deliveryAddressIsNonDefault}
      />
    </>
  );
}
